import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, Inject, OnInit } from '@angular/core';
import { FormControl, UntypedFormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, finalize } from 'rxjs';
import { AppValidators } from 'src/app/common/appValidators';
import { ResourceType } from 'src/app/models/api';
import {
  SingleUserPermissionsActionData,
  UserPermissionsPermissionType,
} from 'src/app/models/api/actions/userPermissions.model';
import { ZenityEntityActionResultCode } from 'src/app/models/api/actions/zenityEntityActions';
import { ParamsForActions } from 'src/app/services/actions/common/common-actions.service';
import {
  ActionsHttpServiceService,
  RequiredParamsForSetAccessHttpRequests,
} from 'src/app/services/httpServices/actionsHttpService/actions-http-service.service';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';
import { Label } from '../../../labels/englishLabels';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-action-set-access',
  templateUrl: './action-set-access.component.html',
  styleUrls: ['./action-set-access.component.scss'],
})
export class ActionSetAccessComponent implements OnInit {
  public emailControl: FormControl;
  public waitingForAddOwnerResponse = false;
  public waitingForRemoveAccessResponse = 0;
  public isWaitingForIsolateResourceResponse = false;

  public isWaitingForListUpdate = true;
  public permissions: SingleUserPermissionsActionData[] | null = null;
  public selectedUsers: SingleUserPermissionsActionData[] = [];

  public isChangeAccessDisabled = false;
  public changeAccessTooltip = '';
  public isolateAgentButtonTooltip = Label.get('commonActions.manageAccessAction.isolateResourceButtonTooltip');
  public isolateResourceButtonDisabled$ = new BehaviorSubject(true);

  constructor(
    private readonly dialogRef: MatDialogRef<ActionSetAccessComponent>,
    @Inject(MAT_DIALOG_DATA)
    private readonly actionsParams: ParamsForActions<RequiredParamsForSetAccessHttpRequests>,
    private readonly _actionsHttpService: ActionsHttpServiceService,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _notificationService: NotificationsService,
    private readonly _destroyRef: DestroyRef
  ) {
    this.dialogRef.disableClose = true;
    this.dialogRef.backdropClick().subscribe(() => {
      this.closeWindow();
    });
  }

  ngOnInit(): void {
    this._initFormControls();
    this._updateUserAccessList();
    this._checkIfChangeAccessIsDisabled();
  }

  public addOwner($event?: MouseEvent): void {
    $event?.stopPropagation();
    const email = this.emailControl.value;
    if (!this.emailControl.valid || email === '') return;

    this.waitingForAddOwnerResponse = true;
    this.isWaitingForIsolateResourceResponse = false;

    this._actionsHttpService
      .addOwner(this.actionsParams.requiredParams, email)
      .pipe(
        finalize(() => (this.waitingForAddOwnerResponse = false)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: () => {
          this._notificationService.success('Owner was added');
          this._finalizeAddOwner();
        },
        error: (httpError: HttpErrorResponse) => {
          if (this._httpErrorHandlerService.isUnhandledExceptionError(httpError.status)) {
            return this._httpErrorHandlerService.handle(httpError);
          } else if (httpError.status == 404) {
            this._notificationService.error(`The email '${email}' is not part of the AAD Tenant`);
            return;
          }

          const text = httpError.error?.detail ?? 'Unspecified';
          this._notificationService.error(`Owner could not be added, reason: ${text}`);
          this._finalizeAddOwner();
        },
      });
  }

  public isIsolateResourceButtonDisabled(): void {
    if (!this.permissions) {
      this.isolateResourceButtonDisabled$.next(true);
      return;
    }
    const hasNonOwnerPermissions = this.permissions.some(
      (permission) => permission.permissionType !== UserPermissionsPermissionType.ownerOf
    );
    this.isolateResourceButtonDisabled$.next(!hasNonOwnerPermissions);
  }

  public openPlatformNewTab(): void {
    window.open(this.actionsParams.optionalParams.platformUrl as string, '_blank');
  }

  public closeWindow() {
    this.dialogRef.close(true);
  }

  public isUserSelected(user: SingleUserPermissionsActionData): boolean {
    return this.selectedUsers.includes(user);
  }

  public toggleUserSelection(userSelection: SingleUserPermissionsActionData): void {
    if (this.isUserSelected(userSelection))
      this.selectedUsers = this.selectedUsers.filter((user) => user !== userSelection);
    else this.selectedUsers = [...this.selectedUsers, userSelection];
  }

  public canRemoveUserAccess(user: SingleUserPermissionsActionData): boolean {
    return user.permissionType !== UserPermissionsPermissionType.ownerOf;
  }

  public removeAccessSelectedUsers(): void {
    this.waitingForRemoveAccessResponse++;

    const idsToRemove = this.selectedUsers.map((user) => user.identityPlatformResourceId);
    this.removeMultipleUsers(idsToRemove);
  }

  public isolateResource($event: MouseEvent): void {
    $event.stopPropagation();
    this.isWaitingForIsolateResourceResponse = true;

    const idsToRemove =
      this.permissions
        ?.filter((user) => user.permissionType !== UserPermissionsPermissionType.ownerOf)
        .map((user) => user.identityPlatformResourceId) ?? [];

    this.removeMultipleUsers(idsToRemove);
  }

  public canRemoveMultipleUsers(): boolean {
    return ![ResourceType.copilot].includes(this.actionsParams.requiredParams.resourceType);
  }

  private removeMultipleUsers(idsToRemove: string[]) {
    const integrationId = this.actionsParams.requiredParams.integrationId;
    const resourceId = this.actionsParams.requiredParams.resourceId;
    if (this.canRemoveMultipleUsers()) {
      this._actionsHttpService.removeUserAccess(integrationId, resourceId, idsToRemove).subscribe({
        next: (response) => {
          this._finalizeRemoveAccess();
          const message = `${response.title}. ${response.description ?? ''}`;
          if (response.resultCode !== ZenityEntityActionResultCode.success)
            return this._notificationService.error(message);
          return this._notificationService.success(message);
        },
        error: (httpError: HttpErrorResponse) => {
          this._finalizeRemoveAccess();
          this._notificationService.error('Failed to remove access. ' + httpError.error?.detail ?? '');
        },
      });
    } else {
      if (!this.isWaitingForIsolateResourceResponse && idsToRemove.length !== 1)
        this._notificationService.info('Selecting multiple items will result in slower execution time');
      this._sendRemoveRequestRecursively(integrationId, resourceId, idsToRemove, 0);
    }
  }

  private _sendRemoveRequestRecursively(
    integrationId: string,
    resourceId: string,
    idsToRemove: string[],
    index: number
  ): void {
    if (index >= idsToRemove.length) {
      this._finalizeRemoveAccess();
      this._notificationService.success('Successfully revoked access');
      if (this.isWaitingForIsolateResourceResponse) this.isWaitingForIsolateResourceResponse = false;
      return;
    }
    this._actionsHttpService.removeUserAccess(integrationId, resourceId, [idsToRemove[index]]).subscribe({
      next: (response) => {
        const message = `${response.title}. ${response.description ?? ''}`;
        if (response.resultCode !== ZenityEntityActionResultCode.success) this._notificationService.error(message);
        this._sendRemoveRequestRecursively(integrationId, resourceId, idsToRemove, index + 1);
      },
      error: (httpError: HttpErrorResponse) => {
        this._notificationService.error('Failed to remove access. ' + httpError.error?.detail ?? '');
      },
    });
  }

  private _initFormControls() {
    this.emailControl = new UntypedFormControl('', [Validators.required, AppValidators.email]);
  }

  private _checkIfChangeAccessIsDisabled(): void {
    if (this.actionsParams.requiredParams.isChangeAccessActionEnabled) return;
    if (this.actionsParams.requiredParams.resourceType == ResourceType.connection) {
      this.isChangeAccessDisabled = true;
      this.changeAccessTooltip = 'Change access is disabled for non-sharable connections';
    }
  }

  private _updateUserAccessList(): void {
    this.isWaitingForListUpdate = true;
    const integrationId = this.actionsParams.requiredParams.integrationId;
    const resourceId = this.actionsParams.requiredParams.resourceId;
    this._actionsHttpService
      .getUserAccessDetails(integrationId, resourceId)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (response) => {
          if (response.resultCode !== ZenityEntityActionResultCode.success) {
            this._notificationService.error(`${response.title}. ${response.description ?? ''}`);
            this.closeWindow();
          } else {
            this.permissions = response.permissions;
            this.isIsolateResourceButtonDisabled();
          }
          this._finalizeUpdateUserAccessList();
        },
        error: (httpError: HttpErrorResponse) => {
          this._notificationService.error('Failed to fetch user permissions. ' + httpError.error?.detail ?? '');
          this._finalizeUpdateUserAccessList();
          this.closeWindow();
        },
      });
  }

  private _finalizeAddOwner() {
    this.emailControl.setValue('');
    this.emailControl.markAsUntouched();
    this._updateUserAccessList();
  }

  private _finalizeRemoveAccess() {
    this.waitingForRemoveAccessResponse--;
    this._updateUserAccessList();
  }

  private _finalizeUpdateUserAccessList() {
    this.isWaitingForListUpdate = false;
    this.selectedUsers = [];
  }
}
